import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, Minus, Plus, ShieldCheck, ShoppingBag, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { fmt, track } from "../lib/api";
import { SITE } from "../config/site";

export function CartDrawer() {
  const { items, subtotal, drawerOpen, setDrawerOpen, setQty, removeItem, verifyGate } = useCart();
  const navigate = useNavigate();
  const belowMin = subtotal < SITE.orderMinimum;
  const missing = SITE.orderMinimum - subtotal;
  const [codeCopied, setCodeCopied] = useState(false);

  const goCheckout = () => {
    verifyGate(() => {
      track("begin_checkout", { subtotal, items: items.length });
      setDrawerOpen(false);
      navigate("/checkout");
    });
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="flex w-full flex-col border-l-brand-line bg-brand-cream p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b border-brand-line bg-white px-6 py-5">
          <SheetTitle className="flex items-center gap-2.5 font-display text-xl font-black text-brand-forest">
            <ShoppingBag className="h-5 w-5" /> Your Cart
            <span className="text-sm font-medium text-brand-ink/50">
              ({items.reduce((n, i) => n + i.qty, 0)} items)
            </span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center" data-testid="cart-empty-state">
            <div className="rounded-full bg-brand-pine/5 p-6">
              <ShoppingBag className="h-8 w-8 text-brand-pine/40" />
            </div>
            <p className="font-display text-lg font-bold text-brand-forest">Your cart is empty</p>
            <p className="text-sm text-brand-ink/60">Browse the menu and find something you'll love.</p>
            <button
              data-testid="cart-continue-shopping-empty"
              onClick={() => { setDrawerOpen(false); navigate("/shop"); }}
              className="mt-2 rounded-full bg-brand-pine px-6 py-3 text-sm font-bold text-brand-cream transition-transform hover:scale-[1.03]"
            >
              Shop the Menu
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <ul className="divide-y divide-brand-line/70">
                {items.map((item) => (
                  <li key={item.key} data-testid={`cart-item-${item.id}`} className="flex gap-4 py-4">
                    <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl">
                      {item.image && (
                        <img src={item.image} alt={item.name} className="img-blend h-full w-full object-cover" loading="lazy" />
                      )}
                    </div>
                    <div className="flex flex-1 flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-bold leading-snug text-brand-ink">{item.name}</p>
                        <button
                          data-testid={`cart-remove-${item.id}`}
                          aria-label={`Remove ${item.name}`}
                          onClick={() => removeItem(item.key)}
                          className="text-brand-ink/40 transition-colors hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {item.optionsDisplay?.length > 0 && (
                        <p className="mt-0.5 text-xs text-brand-ink/55">{item.optionsDisplay.join(" · ")}</p>
                      )}
                      <div className="mt-auto flex items-center justify-between pt-2">
                        <div className="flex items-center rounded-full border border-brand-line bg-white">
                          <button
                            data-testid={`cart-qty-minus-${item.id}`}
                            aria-label="Decrease quantity"
                            onClick={() => setQty(item.key, item.qty - 1)}
                            className="p-1.5 px-2 text-brand-forest"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="w-6 text-center text-sm font-bold" data-testid={`cart-qty-${item.id}`}>{item.qty}</span>
                          <button
                            data-testid={`cart-qty-plus-${item.id}`}
                            aria-label="Increase quantity"
                            onClick={() => setQty(item.key, item.qty + 1)}
                            className="p-1.5 px-2 text-brand-forest"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <p className="font-display text-sm font-black text-brand-forest">{fmt(item.price * item.qty)}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-brand-line bg-white px-6 py-5">
              <div className="flex items-center justify-between">
                <span className="text-sm text-brand-ink/60">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-display text-xl font-black text-brand-forest">
                  {fmt(subtotal)}
                </span>
              </div>
              <div className="mt-4" data-testid="cart-minimum-progress">
                {belowMin ? (
                  <>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-red-600">
                        Add {fmt(missing)} more — {fmt(SITE.orderMinimum)} minimum order
                      </span>
                      <span className="text-brand-ink/50">{fmt(subtotal)} / {fmt(SITE.orderMinimum)}</span>
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-brand-line">
                      <div
                        className="h-full rounded-full bg-red-500 transition-all duration-500"
                        style={{ width: `${Math.min(100, (subtotal / SITE.orderMinimum) * 100)}%` }}
                      />
                    </div>
                  </>
                ) : subtotal < SITE.freeDeliveryMinimum ? (
                  <>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-brand-golddeep">
                        Add {fmt(SITE.freeDeliveryMinimum - subtotal)} more — free DC delivery with code DC80
                      </span>
                      <span className="text-brand-ink/50">{fmt(subtotal)} / {fmt(SITE.freeDeliveryMinimum)}</span>
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-brand-line">
                      <div
                        className="h-full rounded-full bg-brand-gold transition-all duration-500"
                        style={{ width: `${Math.min(100, (subtotal / SITE.freeDeliveryMinimum) * 100)}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <div
                    className="flex items-center justify-between gap-2 rounded-xl border border-brand-moss/30 bg-brand-moss/10 px-3.5 py-2.5"
                    data-testid="free-delivery-unlocked"
                  >
                    <p className="text-xs font-bold leading-snug text-brand-moss">
                      Free DC delivery — use code <span className="font-black tracking-wider">DC80</span> at checkout
                    </p>
                    <button
                      type="button"
                      data-testid="cart-copy-dc80"
                      aria-label="Copy promo code DC80"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText("DC80");
                          setCodeCopied(true);
                          setTimeout(() => setCodeCopied(false), 2000);
                        } catch (_) {}
                      }}
                      className="flex shrink-0 items-center gap-1 rounded-full border border-brand-moss/40 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-moss transition-colors hover:bg-brand-moss hover:text-white"
                    >
                      {codeCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      {codeCopied ? "Copied" : "Copy"}
                    </button>
                  </div>
                )}
              </div>
              <button
                data-testid="checkout-button"
                onClick={goCheckout}
                disabled={belowMin}
                className="mt-4 w-full rounded-full bg-brand-pine py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-cream transition-transform hover:scale-[1.01] hover:bg-brand-forest disabled:cursor-not-allowed disabled:opacity-40"
              >
                {belowMin ? `${fmt(SITE.orderMinimum)} Minimum Order` : "Secure Checkout"}
              </button>
              <button
                data-testid="continue-shopping-button"
                onClick={() => setDrawerOpen(false)}
                className="mt-2.5 w-full rounded-full border border-brand-line py-3.5 text-sm font-medium text-brand-forest transition-colors hover:bg-brand-cream"
              >
                Continue Shopping
              </button>
              <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-brand-ink/45">
                <ShieldCheck className="h-3.5 w-3.5" /> Checkout & payment securely handled · {SITE.ageLine}
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
